import React, { useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, IconButton, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { useFormik } from "formik";
import { useCreateCourseMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import CourseFormBody, {
  CourseFormBodyHandle,
  CourseFormValues,
  DEFAULT_COURSE_FORM_VALUES,
} from "../shared/CourseFormBody";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";
import { surfaceSx } from "../shared/moduleSx";

const CourseCreate: React.FC = () => {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [createCourse, { isLoading }] = useCreateCourseMutation();
  const bodyRef = useRef<CourseFormBodyHandle | null>(null);
  // Once the course row has been persisted, remember its id so a
  // subsequent Save-Course click (e.g. after a partial attachment
  // failure that kept the user on the form) does NOT create a
  // duplicate course. Attachments retry against the existing id
  // instead. Cleared only on unmount or a successful navigation.
  const savedCourseIdRef = useRef<number | null>(null);

  const formik = useFormik<CourseFormValues>({
    initialValues: DEFAULT_COURSE_FORM_VALUES,
    onSubmit: async (values) => {
      // Client-side validation is expressed in-body; the required rule
      // (courseName) is enforced by CourseFormBody's disabled-button guard.
      const payload = {
        courseName: values.courseName.trim(),
        courseCode: values.courseCode.trim() || null,
        description: values.description,
        gradeId: values.gradeId || null,
        subjectId: values.subjectId || null,
        staffId: values.staffId || null,
        status: values.status || "draft",
        startDate: values.startDate || null,
        endDate: values.endDate || null,
        thumbnail: values.thumbnail || null,
      };
      try {
        // Duplicate-course guard: if the previous Save already
        // persisted the course row but attachments failed, skip the
        // create call and jump straight to the upload-retry path.
        let courseIdForSideEffects = savedCourseIdRef.current;
        if (courseIdForSideEffects == null) {
          const res = await createCourse(payload).unwrap();
          if (!res.status) {
            showErrorMessage(res.message || "Failed to save course.");
            return;
          }
          const created = res.data as { id: number } | undefined;
          if (!created?.id) {
            showErrorMessage("Course was created but no course id was returned.");
            return;
          }
          savedCourseIdRef.current = created.id;
          courseIdForSideEffects = created.id;
          // Video URL is only persisted the first time — a second
          // Save (retry) shouldn't create a duplicate video material.
          await bodyRef.current?.persistVideoUrlForCreate(courseIdForSideEffects, values.videoUrl);
        }

        // Attachments are queued locally on the form and get
        // uploaded here against the real course id. `uploadPendingForCreate`
        // returns { ok, failedCount } so we can hold the user on the
        // form when anything fails, without lying about success.
        const upload = await bodyRef.current?.uploadPendingForCreate(courseIdForSideEffects);
        if (upload && !upload.ok) {
          showErrorMessage(
            upload.failedCount === 1
              ? "Course saved, but 1 attachment failed to upload. Use the retry button on the failed row, or click Save course again to retry."
              : `Course saved, but ${upload.failedCount} attachments failed to upload. Use the retry buttons on the failed rows, or click Save course again to retry.`,
          );
          return;
        }
        showMessage("Course created.");
        navigate("/course/list");
      } catch {
        // A thrown exception here means either the create request
        // itself failed (no course row exists) or the upload
        // mutation threw a transport-level error. In both cases the
        // duplicate-course guard above is safe: if `savedCourseIdRef`
        // was already set, the create call was skipped, so no new
        // duplicate can be produced by this catch.
        showErrorMessage(
          savedCourseIdRef.current == null
            ? "Failed to save course."
            : "Course was saved, but an attachment upload could not complete. Please retry.",
        );
      }
    },
  });

  const submitLabel = useMemo(() => (isLoading ? "Saving…" : "Save course"), [isLoading]);

  return (
    // Soft app background (Courses List uses the same tone) so
    // white form cards read as distinct surfaces.
    <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: "#F7F9FC", minHeight: "100%", mx: -2, my: -2, px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      {/* ── PAGE HEADER ────────────────────────────────────────────
             Mirrors the Courses List hero exactly (48 px branded
             icon tile + title/subtitle + soft blue border-shadow) so
             navigating Courses → New Course reads as the same
             module. The back-arrow is now a proper 40 px rounded
             icon button with an aria label. */}
      <Box
        sx={{
          ...surfaceSx,
          p: { xs: 2, md: 2.5 },
          mb: 2,
          borderRadius: 3,
          boxShadow: "0 1px 2px rgba(15,23,42,0.03), 0 8px 24px rgba(31,111,235,0.04)",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title="Back to Courses">
            <IconButton
              aria-label="Back to Courses"
              onClick={() => navigate("/course/list")}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: tint12(B13.primary),
                color: B13.primary,
                transition: "background-color 160ms ease, color 160ms ease",
                "&:hover": {
                  bgcolor: B13.primary,
                  color: "#FFFFFF",
                },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: tint12(B13.primary),
              color: B13.primary,
              "& svg": { fontSize: 24 },
              flexShrink: 0,
            }}
          >
            <MenuBookOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...B13TypeScale.h2, letterSpacing: "-0.01em", lineHeight: 1.15 }}>
              New course
            </Typography>
            <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2, mt: 0.25, maxWidth: 720 }}>
              Describe an offering the school teaches. Save as Draft and publish when it's ready.
            </Typography>
          </Box>
        </Stack>
      </Box>

      <CourseFormBody
        ref={bodyRef}
        mode="create"
        formik={formik}
        submitting={isLoading}
        submitLabel={submitLabel}
      />
    </Box>
  );
};

export default CourseCreate;
